import type { RegistrarDesfechoPayload } from '../types/pesquisaSatisfacaoTypes';

/**
 * Desfecho que não conseguiu chegar ao servidor, guardado para a próxima sessão.
 *
 * Existe por causa de um caso que o design promete e a rede não garante: a pessoa
 * clica na nota, o cartão declara "nota enviada — já está com o time", e o `PATCH`
 * morre porque a conexão caiu. Sem fila, a nota some depois de a UI ter afirmado o
 * contrário.
 *
 * Fica no `localStorage`, não no `sessionStorage`: quem perdeu a conexão pode fechar
 * a aba antes de recuperá-la, e a promessa foi feita mesmo assim. O conteúdo é o
 * mesmo payload do envio — inclui o comentário, que é texto livre; por isso o
 * registro é **apagado assim que o reenvio dá certo** e nunca é logado.
 *
 * É best-effort, não durabilidade: limpar os dados do navegador leva a fila junto.
 * A alternativa (fila no servidor) exigiria persistir a nota antes de saber que ela
 * existe, que é exatamente o que a plataforma não faz por design.
 */
const CHAVE_FILA = 'arb.pesquisaSatisfacao.desfechosPendentes';

/** Teto do que vale reenviar. Uma pesquisa por semana torna fila longa impossível. */
const MAX_PENDENTES = 5;

const ler = (): RegistrarDesfechoPayload[] => {
  if (typeof window === 'undefined') return [];
  try {
    const bruto = window.localStorage.getItem(CHAVE_FILA);
    if (!bruto) return [];
    const valor: unknown = JSON.parse(bruto);
    return Array.isArray(valor) ? (valor as RegistrarDesfechoPayload[]) : [];
  } catch {
    return [];
  }
};

const gravar = (fila: RegistrarDesfechoPayload[]): void => {
  if (typeof window === 'undefined') return;
  try {
    if (fila.length === 0) {
      window.localStorage.removeItem(CHAVE_FILA);
      return;
    }
    window.localStorage.setItem(CHAVE_FILA, JSON.stringify(fila.slice(-MAX_PENDENTES)));
  } catch {
    // Storage bloqueado: perde-se o reenvio, não a sessão de quem está trabalhando.
  }
};

export function enfileirarDesfechoPendente(payload: RegistrarDesfechoPayload): void {
  // Um desfecho por correlação: se o comentário também falhou, ele substitui a nota
  // na fila — o payload dele já carrega a nota, e reenviar os dois duplicaria o
  // evento no painel.
  const semDuplicata = ler().filter((item) => item.correlacaoId !== payload.correlacaoId);
  gravar([...semDuplicata, payload]);
}

export function lerDesfechosPendentes(): RegistrarDesfechoPayload[] {
  return ler();
}

export function removerDesfechoPendente(correlacaoId: string): void {
  gravar(ler().filter((item) => item.correlacaoId !== correlacaoId));
}
